import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Moment } from 'moment';
import { useSnackbar } from 'notistack';
import moment from 'moment-timezone';
import { useTranslation } from 'react-i18next';
import { getDatesRange, getTimezone } from '@mgm/common/utils';
import { usePostHog } from 'posthog-js/react';
import { useAppDispatch, useAppSelector } from '../useAppStore';
import { getAuthenticateUser, getTokenDetails } from '../../store/authenticate/selectors';
import { SchedulesController } from '../../services';
import { useError, usePermissions } from '../useCommon';
import { IBaseResponse, TAxiosError, TGetScheduleRes } from '../../types/responses';
import { schedulesActions } from '../../store/schedules/slice';
import { TAddScheduleShift, TCopyScheduleReq, TEditScheduleShift } from '../../types/requests';
import { commonActions } from '../../store/common/slice';
import { getTimeOffs } from '../../store/schedules/selectors';
import { QUERY_KEYS } from '../../utils/enums';
import { TScheduleShift } from '../../types';

type TUseGetScheduleAPIOptions = {
  isMainSchedulesPage?: boolean
  dates?: [Moment, Moment],
  type?: string
};

export function useGetScheduleDataAPI(options: TUseGetScheduleAPIOptions) {
  const { personnel } = useAppSelector(getAuthenticateUser);
  const { fromDate, toDate } = getDatesRange(options?.dates, false, false, getTimezone(), true);
  const { permissions } = usePermissions();
  const { handleReqError } = useError();
  const dispatch = useAppDispatch();

  const locationIds = permissions.isManager || permissions.isEmployee
    ? personnel?.locations?.map(
      (location) => location.id,
    ) || []
    : [];

  return useQuery(
    [QUERY_KEYS.SCHEDULES, options.type || ''],
    async () => {
      const data = {
        shopName: personnel.shopName,
        timezone: getTimezone(),
        from: fromDate,
        to: toDate,
        locationIds,
      };

      const rsp = await SchedulesController().getScheduleData<TGetScheduleRes>(data);

      if (rsp?.success && 'schedules' in rsp) {
        dispatch(schedulesActions.setScheduleList(rsp.schedules));

        const timeOffs = rsp.timeoffs.map((time) => ({
          ...time,
          start: moment(time.start).startOf('day').format(),
        }));

        dispatch(schedulesActions.setTimeOffs(timeOffs));
      } else if (rsp?.error && rsp.error?.response?.status) {
        handleReqError(rsp.error, data);
      } else if ('msg' in rsp) {
        console.error(`ERROR! get schedules data failed! ${rsp.msg}`);
      }

      return rsp;
    },
    {
      staleTime: options?.type === 'timeoff' ? 0 : 3 * (60 * 1000),
      suspense: false,
      refetchOnWindowFocus: false,
      refetchIntervalInBackground: false,
      enabled: options?.isMainSchedulesPage,
    },
  );
}

export function useAddScheduleShiftAPI(isDisplayToast = true) {
  const { personnel } = useAppSelector(getAuthenticateUser);
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();
  const { handleReqError } = useError();
  const { t } = useTranslation('schedule');
  const posthog = usePostHog();
  const { refetch } = useGetScheduleDataAPI({
    isMainSchedulesPage: false,
  });

  const addScheduleShiftApi = (data: Omit<TAddScheduleShift, 'shopName'>) => {
    const addShiftData = {
      shopName: personnel.shopName,
      schedulesData: Array.isArray(data.schedulesData) ? data.schedulesData : [data.schedulesData],
      notifyEmployees: data.notifyEmployees,
      locals: data.locals,
    };

    return SchedulesController().addScheduleShift<IBaseResponse>(addShiftData);
  };

  return useMutation(addScheduleShiftApi, {
    onSuccess: async (rsp, variables) => {
      if (rsp.success) {
        dispatch(commonActions.setIsOpenPagesNav(false));

        setTimeout(() => {
          dispatch(schedulesActions.setSelectedScheduleShift({} as TScheduleShift));
        }, 300);

        posthog.capture('ManageMate_web', {
          email: personnel?.email,
          shopName: personnel?.shopName,
          action: 'user_added_schedules',
        });

        if (isDisplayToast) {
          enqueueSnackbar(`${t('shiftWasAdded')}`, { variant: 'success' });
        }
        await refetch();
      } else if (rsp?.error && rsp.error?.response?.status) {
        handleReqError(rsp.error, variables);
      } else if ('msg' in rsp) {
        console.error(`ERROR! add schedule shift failed! ${rsp.msg}`);
      }
    },
    onError: (error: TAxiosError) => {
      console.error(`ERROR! add schedule shift request threw an Exception! ${error}`);
    },
  });
}

export function useEditScheduleShiftAPI() {
  const { personnel } = useAppSelector(getAuthenticateUser);
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation('schedule');
  const dispatch = useAppDispatch();
  const { handleReqError } = useError();
  const { refetch } = useGetScheduleDataAPI({
    isMainSchedulesPage: false,
  });

  const editScheduleShiftApi = (data: Omit<TEditScheduleShift, 'shopName'>) => {
    const editShiftData = {
      shopName: personnel.shopName,
      schedulesData: Array.isArray(data.schedulesData) ? data.schedulesData : [data.schedulesData],
    };

    return SchedulesController().editScheduleShift<IBaseResponse>(editShiftData);
  };

  return useMutation(editScheduleShiftApi, {
    onSuccess: async (rsp, variables) => {
      if (rsp?.success) {
        dispatch(commonActions.setIsOpenPagesNav(false));

        setTimeout(() => {
          dispatch(schedulesActions.setSelectedScheduleShift({} as TScheduleShift));
        }, 300);

        enqueueSnackbar(`${t('shiftWasUpdated')}`, { variant: 'success' });
        await refetch();
      } else if (rsp?.error && rsp.error?.response?.status) {
        handleReqError(rsp.error, variables);
      } else if ('msg' in rsp) {
        console.error(`ERROR! add schedule shift failed! ${rsp.msg}`);
      }
    },
    onError: (error: TAxiosError) => {
      console.error(`ERROR! edit schedule shift request threw an Exception! ${error}`);
    },
  });
}

export function useDeleteScheduleShiftAPI() {
  const { handleReqError } = useError();
  const { personnel } = useAppSelector(getAuthenticateUser);
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation('schedule');
  const { refetch } = useGetScheduleDataAPI({
    isMainSchedulesPage: false,
  });

  const deleteShiftApi = (shiftData: { id: number, start: string }) => {
    const data = {
      shopName: personnel.shopName,
      schedulesIds: Array.isArray(shiftData.id) ? shiftData.id : [shiftData.id],
    };

    return SchedulesController().deleteScheduleShift<IBaseResponse>(data);
  };

  return useMutation(deleteShiftApi, {
    onSuccess: async (rsp, variables) => {
      if (rsp?.success) {
        enqueueSnackbar(`${t('shiftWasDeleted')}`, {
          variant: 'success',
        });

        await refetch();
      } else if (rsp?.error && rsp.error?.response?.status) {
        handleReqError(rsp.error, variables);
      } else if ('msg' in rsp) {
        console.error(`ERROR! delete schedule shift failed! ${rsp.msg}`);
      }

      return rsp;
    },
    onError: (error: TAxiosError) => {
      console.error(`ERROR! delete schedule shift request threw an Exception! ${error}`);
    },
  });
}

export function useRequestDayOffAPI() {
  const { personnel } = useAppSelector(getAuthenticateUser);
  const tokenDetails = useAppSelector(getTokenDetails);
  const { t } = useTranslation('schedule');
  const { enqueueSnackbar } = useSnackbar();
  const posthog = usePostHog();
  const dispatch = useAppDispatch();
  const { refetch } = useGetScheduleDataAPI({
    isMainSchedulesPage: false,
  });

  const { handleReqError } = useError();

  const requestDayOffApi = (payload: {
    days: Array<{ date: string, type: string }>,
    note: string }) => {
    const data = {
      ...payload,
      shopName: personnel.shopName,
      permissionLevel: personnel.permissionLevel,
      timezone: getTimezone(),
      creator: {
        name: personnel.name,
        email: personnel.email,
        avatarFile: personnel.avatarFile || '',
        locale: tokenDetails.settings?.ui?.locale || 'en',
      },
      requestedDaysAmount: payload.days.length,
      locationIds: personnel.locations?.map(
        (location) => location.id,
      ),
    };

    return SchedulesController().requestTimeOff<IBaseResponse>(data);
  };

  return useMutation(requestDayOffApi, {
    onSuccess: async (rsp, variables) => {
      if (rsp?.success) {
        await refetch();
        enqueueSnackbar(`${t('timeoffRequestSubmitted')}`, {
          variant: 'success',
        });

        posthog.capture('ManageMate_web', {
          email: personnel?.email,
          shopName: personnel?.shopName,
          action: 'user_request_day_off',
        });

        dispatch(schedulesActions.setIsOpenDayOffNav(false));
      } else if (rsp?.error && rsp.error?.response?.status) {
        handleReqError(rsp.error, variables);
      } else if ('msg' in rsp) {
        console.error(`ERROR! request day off failed! ${rsp.msg}`);
      }

      return rsp;
    },
    onError: (error: TAxiosError) => {
      console.error(`ERROR! request day off request threw an Exception! ${error}`);
    },
  });
}

export function useApproveTimeOffAPI() {
  const { personnel } = useAppSelector(getAuthenticateUser);
  const timeOffs = useAppSelector(getTimeOffs);
  const dispatch = useAppDispatch();
  const { handleReqError } = useError();
  const posthog = usePostHog();

  const approveTimeOffApi = (payload:
  { timeoffId: string | number,
    comment: string }) => {
    const data = {
      ...payload,
      shopName: personnel.shopName,
    };

    return SchedulesController().approveTimeOff<IBaseResponse>(data);
  };

  return useMutation(approveTimeOffApi, {
    onSuccess: (rsp, variables) => {
      if (rsp.success) {
        dispatch(schedulesActions.setTimeOffs(
          timeOffs.map((time) => (time.id === Number(variables.timeoffId) ? {
            ...time, status: 'Approved', answered: true, approved: true,
          } : time)),
        ));

        posthog.capture('ManageMate_web', {
          email: personnel?.email,
          shopName: personnel?.shopName,
          action: 'user_approved_day_off',
        });
      } else if (rsp?.error && rsp.error?.response?.status) {
        handleReqError(rsp.error, variables);
      } else if ('msg' in rsp) {
        console.error(`ERROR! approve day off failed! ${rsp.msg}`);
      }
    },
    onError: (error: TAxiosError) => {
      console.error(`ERROR! approve day off request threw an Exception! ${error}`);
    },
  });
}

export function useDeclineTimeOffAPI() {
  const { personnel } = useAppSelector(getAuthenticateUser);
  const timeOffs = useAppSelector(getTimeOffs);
  const dispatch = useAppDispatch();
  const { handleReqError } = useError();
  const posthog = usePostHog();

  const declineTimeOffApi = (payload:
  { timeoffId: string | number,
    comment: string }) => {
    const data = {
      ...payload,
      shopName: personnel.shopName,
    };

    return SchedulesController().declineTimeOff<IBaseResponse>(data);
  };

  return useMutation(declineTimeOffApi, {
    onSuccess: (rsp, variables) => {
      if (rsp.success) {
        dispatch(schedulesActions.setTimeOffs(
          timeOffs.map((time) => (time.id === Number(variables.timeoffId) ? { ...time, status: 'Declined', answered: true } : time)),
        ));

        posthog.capture('ManageMate_web', {
          email: personnel?.email,
          shopName: personnel?.shopName,
          action: 'user_declined_day_off',
        });
      } else if (rsp?.error && rsp.error?.response?.status) {
        handleReqError(rsp.error, variables);
      } else if ('msg' in rsp) {
        console.error(`ERROR! decline day off failed! ${rsp.msg}`);
      }
    },
    onError: (error: TAxiosError) => {
      console.error(`ERROR! decline day off request threw an Exception! ${error}`);
    },
  });
}

export function useDeleteTimeOffAPI() {
  const { personnel } = useAppSelector(getAuthenticateUser);
  const { t } = useTranslation('schedule');
  const timeOffs = useAppSelector(getTimeOffs);
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();
  const { handleReqError } = useError();

  const deleteTimeOffApi = (timeOffData: { id: number }) => {
    const data = {
      shopName: personnel.shopName,
      timeoffId: timeOffData.id,
    };

    return SchedulesController().deleteTimeoff<IBaseResponse>(data);
  };

  return useMutation(deleteTimeOffApi, {
    onSuccess: async (rsp, variables) => {
      if (rsp?.success) {
        enqueueSnackbar(`${t('timeoffWasDeleted')}`, {
          variant: 'success',
        });

        dispatch(schedulesActions.setTimeOffs(timeOffs.filter((time) => time.id !== variables.id)));
      } else if (rsp?.error && rsp.error?.response?.status) {
        handleReqError(rsp.error, variables);
      } else if ('msg' in rsp) {
        console.error(`ERROR! delete timeoff shift failed! ${rsp.msg}`);
      }

      return rsp;
    },
    onError: (error: TAxiosError) => {
      console.error(`ERROR! delete timeoff shift request threw an Exception! ${error}`);
    },
  });
}

export function useSendScheduleAPI() {
  const { personnel } = useAppSelector(getAuthenticateUser);
  const { enqueueSnackbar } = useSnackbar();
  const { handleReqError } = useError();
  const { t } = useTranslation('schedule');
  const posthog = usePostHog();

  const sendScheduleApi = (data: Omit<TAddScheduleShift, 'shopName'>) => {
    const scheduleData = {
      shopName: personnel.shopName,
      schedulesData: data.schedulesData,
      locals: data.locals,
    };

    return SchedulesController().sendSchedule<IBaseResponse>(scheduleData);
  };

  return useMutation(sendScheduleApi, {
    onSuccess: async (rsp, variables) => {
      if (rsp.success) {
        enqueueSnackbar(`${t('weeklyScheduleEmailedToTeam')}`, { variant: 'success' });

        posthog.capture('ManageMate_web', {
          email: personnel?.email,
          shopName: personnel?.shopName,
          action: 'user_send_schedule',
        });
      } else if (rsp?.error && rsp.error?.response?.status) {
        handleReqError(rsp.error, variables);
      } else if ('msg' in rsp) {
        console.error(`ERROR! send schedule failed! ${rsp.msg}`);
      }
    },
    onError: (error: TAxiosError) => {
      console.error(`ERROR! send schedule request threw an Exception! ${error}`);
    },
  });
}

export function useUpsertNoteAPI() {
  const { personnel } = useAppSelector(getAuthenticateUser);
  const { enqueueSnackbar } = useSnackbar();
  const { handleReqError } = useError();
  const queryClient = useQueryClient();
  const { t } = useTranslation('schedule');
  const posthog = usePostHog();

  const upsertNoteApi = (data: { note: string, scheduleId: number }) => {
    const noteData = {
      shopName: personnel.shopName,
      note: data.note,
      scheduleId: data.scheduleId,
      timezone: getTimezone(),
    };

    return SchedulesController().upsertNote<IBaseResponse>(noteData);
  };

  return useMutation(upsertNoteApi, {
    onSuccess: async (rsp, variables) => {
      if (rsp.success) {
        enqueueSnackbar(`${t('noteWasAdded')}`, { variant: 'success' });

        posthog.capture('ManageMate_web', {
          email: personnel?.email,
          shopName: personnel?.shopName,
          action: 'user_add_note',
        });

        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.SCHEDULES] });
      } else if (rsp?.error && rsp.error?.response?.status) {
        handleReqError(rsp.error, variables);
      } else if ('msg' in rsp) {
        console.error(`ERROR! upsert note failed! ${rsp.msg}`);
      }
    },
    onError: (error: TAxiosError) => {
      console.error(`ERROR! upsert note request threw an Exception! ${error}`);
    },
  });
}

export function useDeleteNoteAPI() {
  const { personnel } = useAppSelector(getAuthenticateUser);
  const { enqueueSnackbar } = useSnackbar();
  const { handleReqError } = useError();
  const queryClient = useQueryClient();
  const { t } = useTranslation('schedule');

  const deleteNoteApi = (data: { scheduleId: number }) => {
    const noteData = {
      shopName: personnel.shopName,
      scheduleId: data.scheduleId,
    };

    return SchedulesController().deleteNote<IBaseResponse>(noteData);
  };

  return useMutation(deleteNoteApi, {
    onSuccess: async (rsp, variables) => {
      if (rsp.success) {
        enqueueSnackbar(`${t('noteWasDeleted')}`, { variant: 'success' });
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.SCHEDULES] });
      } else if (rsp?.error && rsp.error?.response?.status) {
        handleReqError(rsp.error, variables);
      } else if ('msg' in rsp) {
        console.error(`ERROR! delete note failed! ${rsp.msg}`);
      }
    },
    onError: (error: TAxiosError) => {
      console.error(`ERROR! delete note request threw an Exception! ${error}`);
    },
  });
}

export function useCopyScheduleAPI() {
  const { personnel } = useAppSelector(getAuthenticateUser);
  const { enqueueSnackbar } = useSnackbar();
  const { handleReqError } = useError();
  const { t } = useTranslation('schedule');
  const posthog = usePostHog();

  const copyScheduleApi = (data: Omit<TCopyScheduleReq, 'shopName' | 'timezone'>) => {
    const scheduleData = {
      ...data,
      shopName: personnel.shopName,
      timezone: getTimezone(),
    };

    return SchedulesController().copySchedule<IBaseResponse>(scheduleData);
  };

  return useMutation(copyScheduleApi, {
    onSuccess: async (rsp, variables) => {
      if (rsp.success) {
        const start = moment(variables.from).format('DD/MM');
        const end = moment(variables.to).format('DD/MM');
        const copyToStart = moment(variables.copyTo).format('DD/MM');
        const dayDiff = moment(variables.to).diff(moment(variables.from), 'days');
        const copyToEnd = moment(variables.copyTo).add(dayDiff, 'days').format('DD/MM');

        posthog.capture('ManageMate_web', {
          email: personnel?.email,
          shopName: personnel?.shopName,
          action: 'user_copied_schedules',
        });

        enqueueSnackbar(`${t('scheduleFrom')} ${start} - ${end} ${t('copiedTo')} ${copyToStart} - ${copyToEnd}`, {
          variant: 'success',
        });
      } else if (rsp?.error && rsp.error?.response?.status) {
        handleReqError(rsp.error, variables);
      } else if ('msg' in rsp) {
        console.error(`ERROR! copy schedule failed! ${rsp.msg}`);
      }
    },
    onError: (error: TAxiosError) => {
      console.error(`ERROR! copy schedule request threw an Exception! ${error}`);
    },
  });
}
